import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import AdminTable from "../../../components/AdminTable";
import { API_DOMAIN } from "config";
import { formatDate } from "../../../utils/formatDate";
import "./AdminAuctions.css";

const statusCopy = {
  future: { label: "עתידי", className: "bg-sky-50 text-sky-700 border-sky-200" },
  active: { label: "פעיל", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ended: { label: "הסתיים ללא זוכה", className: "bg-slate-100 text-slate-700 border-slate-300" },
  winner: { label: "זוכה במכרז", className: "bg-amber-50 text-amber-700 border-amber-200" },
  finalized: { label: "נסגר", className: "bg-slate-100 text-slate-700 border-slate-300" },
  cancelled: { label: "בוטל", className: "bg-rose-50 text-rose-700 border-rose-200" },
  unknown: { label: "לא ידוע", className: "bg-zinc-50 text-zinc-600 border-zinc-200" },
};

function money(value) {
  const num = Number(value);
  if (!Number.isFinite(num) || num <= 0) return "—";
  return num.toLocaleString("he-IL", {
    style: "currency",
    currency: "ILS",
    maximumFractionDigits: 0,
  });
}

function plainNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) ? num.toLocaleString("he-IL") : "0";
}

function clean(value, fallback = "—") {
  return value === null || value === undefined || value === "" ? fallback : value;
}

function holdStatusText(value) {
  return String(value || "").toLowerCase() === "active" ? "פעיל" : clean(value);
}

function holdReasonText(value) {
  return value === "auction_loser_after_winner_selected" ? "לא זכה במכרז" : clean(value);
}

function formatDateTime(value) {
  if (!value) return "—";
  return formatDate(value);
}

function StatusBadge({ status, hasWinner = false }) {
  const displayStatus = ["ended", "finalized"].includes(status) && hasWinner ? "winner" : status;
  const item = statusCopy[displayStatus] || statusCopy.unknown;
  return (
    <div className="flex flex-col items-start gap-1">
      <span className={`inline-flex min-w-[76px] justify-center rounded-full border px-3 py-1 text-xs font-semibold ${item.className}`}>
        {item.label}
      </span>
    </div>
  );
}

function MiniMetric({ label, value, tone = "slate" }) {
  const tones = {
    slate: "border-slate-200 bg-white text-slate-800",
    sky: "border-sky-200 bg-sky-50 text-sky-800",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
    amber: "border-amber-200 bg-amber-50 text-amber-800",
    rose: "border-rose-200 bg-rose-50 text-rose-800",
  };

  return (
    <div className={`admin-auctions-metric rounded-lg border px-4 py-3 ${tones[tone] || tones.slate}`} data-tone={tone}>
      <div className="admin-auctions-metric-label text-xs font-medium opacity-70">{label}</div>
      <div className="admin-auctions-metric-value mt-1 text-2xl font-bold">{plainNumber(value)}</div>
    </div>
  );
}

function DetailLine({ label, value }) {
  return (
    <div className="flex flex-col gap-1 rounded-md border border-slate-200 bg-slate-50 px-3 py-2">
      <span className="text-[11px] font-medium text-slate-500">{label}</span>
      <span className="text-sm font-semibold text-slate-800">{clean(value)}</span>
    </div>
  );
}

function SectionTitle({ children }) {
  return <h3 className="text-sm font-bold text-slate-900">{children}</h3>;
}

export default function AdminAuctions() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({});
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    status: "all",
    provider_id: "",
    date_from: "",
    date_to: "",
    has_bids: "",
    has_winner: "",
  });
  const [detailId, setDetailId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  const loadProviders = useCallback(async () => {
    try {
      const res = await fetch(`${API_DOMAIN}/admin/getUsers?type=providers`);
      const data = await res.json();
      setProviders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setProviders([]);
    }
  }, []);

  const loadAuctions = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const qs = new URLSearchParams();
      qs.set("page_size", "500");
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== "" && value !== "all") {
          qs.set(key, value);
        }
      });

      const res = await fetch(`${API_DOMAIN}/admin/auctions?${qs.toString()}`);
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }

      setRows(Array.isArray(data?.rows) ? data.rows : []);
      setStats(data?.stats || {});
    } catch (err) {
      console.error(err);
      setRows([]);
      setStats({});
      setError("שגיאה בטעינת נתוני המכרזים");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  useEffect(() => {
    loadAuctions();
  }, [loadAuctions]);

  const openDetail = async (id) => {
    setDetailId(id);
    setDetail(null);
    setDetailError("");
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_DOMAIN}/admin/auctions/${id}`);
      const data = await res.json();
      if (!res.ok || data?.success === false) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      setDetail(data);
    } catch (err) {
      console.error(err);
      setDetailError("שגיאה בטעינת פירוט המכרז");
    } finally {
      setDetailLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { name: "ID", selector: (r) => r.id, width: "78px", sortable: true },
      { name: "מס׳ רכב", selector: (r) => r.license_plate, width: "115px", sortable: true },
      {
        name: "רכב",
        selector: (r) => `${clean(r.manufacturer, "")} ${clean(r.model, "")} ${clean(r.year, "")}`,
        sortable: true,
        grow: 1.5,
        wrap: true,
        cell: (r) => (
          <div className="text-right">
            <div className="font-semibold text-slate-800">
              {[r.manufacturer, r.model, r.sub_model].filter(Boolean).join(" ") || "—"}
            </div>
            <div className="text-xs text-slate-500">{clean(r.year)}</div>
          </div>
        ),
      },
      {
        name: "ספק",
        selector: (r) => r.provider_name,
        sortable: true,
        grow: 1.3,
        wrap: true,
        cell: (r) => (
          <div className="text-right">
            <div className="font-semibold text-slate-800">{clean(r.provider_name)}</div>
            <div className="text-xs text-slate-500">{clean(r.provider_phone, "")}</div>
          </div>
        ),
      },
      {
        name: "סטטוס",
        selector: (r) => r.computed_status,
        sortable: true,
        width: "125px",
        cell: (r) => (
          <StatusBadge
            status={r.computed_status}
            hasWinner={Boolean(r.auction_winner_user_id || r.auction_winning_offer_id)}
          />
        ),
      },
      {
        name: "התחלה",
        selector: (r) => r.auction_start_datetime,
        sortable: true,
        width: "145px",
        cell: (r) => formatDateTime(r.auction_start_datetime),
      },
      {
        name: "סיום",
        selector: (r) => r.auction_active_until,
        sortable: true,
        width: "145px",
        cell: (r) => formatDateTime(r.auction_active_until || r.auction_ended_at),
      },
      {
        name: "מספר הצעות",
        selector: (r) => Number(r.bid_count || 0),
        sortable: true,
        width: "115px",
        cell: (r) => <span className="font-bold text-slate-800">{plainNumber(r.bid_count)}</span>,
      },
      {
        name: "סכום שזכה",
        selector: (r) => Number(r.winning_offer_amount || 0),
        sortable: true,
        width: "125px",
        cell: (r) => <span className="font-bold text-slate-900">{money(r.winning_offer_amount)}</span>,
      },
      {
        name: "זוכה",
        selector: (r) => r.winner_business_name || r.winner_contact_name || r.winner_name,
        sortable: true,
        grow: 1.1,
        wrap: true,
        cell: (r) => clean(r.winner_business_name || r.winner_contact_name || r.winner_name),
      },
      {
        name: "פירוט",
        width: "190px",
        cell: (r) => (
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => openDetail(r.id)}
              className="whitespace-nowrap rounded-full border border-slate-300 bg-white px-3 py-1 text-xs font-semibold text-slate-700 transition hover:bg-slate-100"
            >
              פרטים
            </button>
            {!(r.computed_status === "ended" && !r.auction_winner_user_id && !r.auction_winning_offer_id) && (
              <button
                type="button"
                onClick={() => navigate(`/admin/auctions/${r.id}`)}
                className="admin-auction-view-button inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold transition"
              >
                <svg aria-hidden="true" viewBox="0 0 24 24" className="h-4 w-4" fill="none">
                  <path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z" stroke="currentColor" strokeWidth="1.7" />
                  <circle cx="12" cy="12" r="2.5" stroke="currentColor" strokeWidth="1.7" />
                </svg>
                צפה במכרז
              </button>
            )}
          </div>
        ),
      },
    ],
    [navigate]
  );

  const exportKeys = [
    "id",
    "license_plate",
    "manufacturer",
    "model",
    "year",
    "provider_name",
    "computed_status",
    "raw_auction_status",
    "auction_start_datetime",
    "auction_active_until",
    "bid_count",
    "winning_offer_amount",
    "winner_name",
    "auction_finalized_at",
  ];

  const searchKeys = [
    "id",
    "license_plate",
    "manufacturer",
    "model",
    "year",
    "provider_name",
    "winner_name",
    "computed_status",
  ];

  const tableFilters = (
    <div className="flex flex-wrap items-center justify-end gap-2">
      <select value={filters.status} onChange={(e) => setFilters((p) => ({ ...p, status: e.target.value }))} className="admin-select w-[120px] rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs">
        <option value="all">כל הסטטוסים</option>
        <option value="future">עתידי</option>
        <option value="active">פעיל</option>
        <option value="winner">זוכה במכרז</option>
        <option value="ended_no_winner">הסתיים ללא זוכה</option>
        <option value="finalized">נסגר</option>
        <option value="cancelled">בוטל</option>
      </select>
      <select value={filters.provider_id} onChange={(e) => setFilters((p) => ({ ...p, provider_id: e.target.value }))} className="admin-select w-[145px] rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs">
        <option value="">כל הספקים</option>
        {providers.map((provider) => (
          <option key={provider.id} value={provider.id}>{provider.name || `#${provider.id}`}</option>
        ))}
      </select>
      <select value={filters.has_bids} onChange={(e) => setFilters((p) => ({ ...p, has_bids: e.target.value }))} className="admin-select w-[115px] rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs">
        <option value="">כל ההצעות</option>
        <option value="1">יש הצעות</option>
        <option value="0">ללא הצעות</option>
      </select>
      <select value={filters.has_winner} onChange={(e) => setFilters((p) => ({ ...p, has_winner: e.target.value }))} className="admin-select w-[110px] rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs">
        <option value="">כל הזוכים</option>
        <option value="1">יש זוכה</option>
        <option value="0">ללא זוכה</option>
      </select>
      <input type="date" value={filters.date_from} onChange={(e) => setFilters((p) => ({ ...p, date_from: e.target.value }))} className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700" />
      <input type="date" value={filters.date_to} onChange={(e) => setFilters((p) => ({ ...p, date_to: e.target.value }))} className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700" />
      <button
        type="button"
        onClick={() => setFilters({ status: "all", provider_id: "", date_from: "", date_to: "", has_bids: "", has_winner: "" })}
        className="rounded-full border border-slate-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-100"
      >
        איפוס
      </button>
    </div>
  );

  return (
    <div className="admin-auctions-page min-h-[calc(100vh-4rem)] bg-slate-100 p-4" dir="rtl">
      <div className="admin-auctions-summary mb-4 grid gap-3">
        <MiniMetric label="מכרזים עתידיים" value={stats.future} tone="sky" />
        <MiniMetric label="מכרזים פעילים" value={stats.active} tone="emerald" />
        <MiniMetric label="מכרזים עם זוכה" value={stats.with_winner} tone="amber" />
        <MiniMetric label="נסגרו" value={stats.finalized} />
      </div>

      {error && (
        <div className="mb-3 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm font-medium text-rose-700">
          {error}
        </div>
      )}

      <AdminTable
        title="ניהול מכרזים"
        subtitle="תצוגת מנהלים מלאה למעקב אחר מכרזים, הצעות, J5 והתראות"
        rows={rows}
        columns={columns}
        searchKeys={searchKeys}
        exportKeys={exportKeys}
        loading={loading}
        filters={tableFilters}
        pageSizeOptions={[10, 20, 50, 100]}
        extraActions={
          <button
            type="button"
            onClick={loadAuctions}
            className="admin-auctions-refresh rounded-full bg-slate-700 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-slate-600"
          >
            רענון
          </button>
        }
      />

      {detailId && (
        <AuctionDetailModal
          detail={detail}
          loading={detailLoading}
          error={detailError}
          onClose={() => {
            setDetailId(null);
            setDetail(null);
          }}
        />
      )}
    </div>
  );
}

function AuctionDetailModal({ detail, loading, error, onClose }) {
  const auction = detail?.auction;
  const summary = detail?.summary || {};
  const bids = detail?.bids || [];
  const offers = detail?.offers || [];
  const holds = detail?.reservation_holds || [];

  const winningOffer = offers.find((offer) =>
    Number(offer.id) === Number(auction?.auction_winning_offer_id)
  ) || offers.find((offer) => Number(offer.status) === 2);

  const winnerName = auction?.winner_business_name || auction?.winner_contact_name || winningOffer?.dealer_business_name || winningOffer?.dealer_contact_name || auction?.winner_name || winningOffer?.dealer_name;
  const winnerPhone = auction?.winner_phone || winningOffer?.dealer_phone;
  const winningAmount = auction?.winning_offer_amount || winningOffer?.offered_price;

  const auctionOffers = [
    ...bids.map((bid) => ({
      key: `bid-${bid.id}`,
      userId: bid.user_id,
      name: bid.bidder_business_name || bid.bidder_contact_name || bid.bidder_name,
      phone: bid.bidder_phone,
      amount: bid.bid_amount,
      date: bid.created_at,
      holdStatus: bid.hold_status,
    })),
    ...offers
      .filter((offer) => !bids.some((bid) =>
        Number(bid.user_id) === Number(offer.user_id) &&
        Number(bid.bid_amount) === Number(offer.offered_price)
      ))
      .map((offer) => ({
        key: `offer-${offer.id}`,
        userId: offer.user_id,
        name: offer.dealer_business_name || offer.dealer_contact_name || offer.dealer_name,
        phone: offer.dealer_phone,
        amount: offer.offered_price,
        date: offer.creation_date,
        holdStatus: offer.reservation_hold_status,
      })),
  ].sort((a, b) => Number(b.amount || 0) - Number(a.amount || 0));

  const participantCount = new Set(auctionOffers.map((offer) => Number(offer.userId)).filter(Boolean)).size;
  const auctionDisplayStatus = ["ended", "finalized"].includes(auction?.computed_status) && (winnerName || winningAmount)
    ? "winner"
    : auction?.computed_status;

  const timeline = [
    ["נוצר", auction?.creation_date],
    ["תחילת מכרז", auction?.auction_start_datetime],
    ["סיום פעיל עד", auction?.auction_active_until],
    ["הסתיים", auction?.auction_ended_at],
    ["נסגר", auction?.auction_finalized_at],
  ].filter(([, value]) => value);

  return (
    <div className="admin-auction-modal fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm" dir="rtl">
      <div className="admin-auction-modal-panel w-full max-w-5xl overflow-y-auto rounded-2xl bg-white shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-slate-200 bg-white px-5 py-4"
        style={{background: 'white'}}>
          <div>
            <h2 className="text-xl font-bold text-slate-900">פירוט מכרז</h2>
            <p className="mt-1 text-xs text-slate-500">
              {auction ? `רכב #${auction.id} · ${clean(auction.license_plate, "ללא מספר רכב")}` : "טוען נתונים"}
            </p>
          </div>
          <button type="button" onClick={onClose} className="h-9 w-9 rounded-full bg-slate-100 text-lg font-bold text-slate-600 hover:bg-slate-200">
            ×
          </button>
        </div>

        {loading && <div className="admin-auction-modal-loading flex items-center justify-center p-8 text-center text-sm text-slate-500">טוען פירוט מכרז...</div>}
        {error && <div className="m-5 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-medium text-rose-700">{error}</div>}

        {!loading && !error && auction && (
          <div className="space-y-6 p-5">
            <div className="admin-auction-detail-grid grid gap-3">
              <DetailLine label="סטטוס" value={statusCopy[auctionDisplayStatus]?.label || auctionDisplayStatus} />
              <DetailLine label="מספר משתתפים" value={plainNumber(participantCount || summary.bid_count)} />
              <DetailLine label="הצעה שזכתה" value={money(winningAmount)} />
              <DetailLine label="רכב" value={[auction.manufacturer, auction.model, auction.sub_model, auction.year].filter(Boolean).join(" ")} />
              <DetailLine label="מספר רכב" value={auction.license_plate} />
              <DetailLine label="מחיר מבוקש" value={money(auction.asking_price)} />
              <DetailLine label="ספק" value={auction.provider_name} />
              <DetailLine label="איש קשר ספק" value={auction.provider_contact_name} />
              <DetailLine label="טלפון ספק" value={auction.provider_phone} />
              <DetailLine label="זוכה" value={winnerName} />
              <DetailLine label="טלפון זוכה" value={winnerPhone} />
              <DetailLine label="הצעה זוכה" value={money(winningAmount)} />
            </div>

            <div className="space-y-3">
              <SectionTitle>ציר זמן</SectionTitle>
              <div className="admin-auction-timeline-grid grid gap-2">
                {timeline.map(([label, value]) => (
                  <div key={label} className="rounded-lg border border-slate-200 bg-white px-3 py-3">
                    <div className="text-xs font-medium text-slate-500">{label}</div>
                    <div className="mt-1 text-sm font-semibold text-slate-800">{formatDateTime(value)}</div>
                  </div>
                ))}
              </div>
            </div>

            <DataSection title="הצעות מכרז" empty="אין הצעות מכרז">
              <table className="w-full min-w-[760px] text-right text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2">מיקום</th>
                    <th className="px-3 py-2">סוחר</th>
                    <th className="px-3 py-2">טלפון</th>
                    <th className="px-3 py-2">סכום</th>
                    <th className="px-3 py-2">תאריך</th>
                    <th className="px-3 py-2">J5</th>
                  </tr>
                </thead>
                <tbody>
                  {auctionOffers.map((offer, index) => (
                    <tr key={offer.key} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-semibold">{index + 1}</td>
                      <td className="px-3 py-2">{clean(offer.name)}</td>
                      <td className="px-3 py-2">{clean(offer.phone)}</td>
                      <td className="px-3 py-2 font-bold">{money(offer.amount)}</td>
                      <td className="px-3 py-2">{formatDateTime(offer.date)}</td>
                      <td className="px-3 py-2">{holdStatusText(offer.holdStatus)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataSection>

            <DataSection title="שריונים / שחרורים" empty="אין שריונים או שחרורים">
              <table className="w-full min-w-[760px] text-right text-sm">
                <thead className="bg-slate-50 text-xs text-slate-500">
                  <tr>
                    <th className="px-3 py-2">ID</th>
                    <th className="px-3 py-2">סוחר</th>
                    <th className="px-3 py-2">סטטוס</th>
                    <th className="px-3 py-2">סכום</th>
                    <th className="px-3 py-2">תוקף</th>
                    <th className="px-3 py-2">שוחרר</th>
                    <th className="px-3 py-2">סיבה</th>
                  </tr>
                </thead>
                <tbody>
                  {holds.map((hold) => (
                    <tr key={hold.id} className="border-t border-slate-100">
                      <td className="px-3 py-2 font-semibold">{hold.id}</td>
                      <td className="px-3 py-2">{clean(hold.holder_business_name || hold.holder_contact_name || hold.holder_name)}</td>
                      <td className="px-3 py-2">{holdStatusText(hold.status)}</td>
                      <td className="px-3 py-2">{money(hold.amount)}</td>
                      <td className="px-3 py-2">{formatDateTime(hold.hold_expires_at)}</td>
                      <td className="px-3 py-2">{formatDateTime(hold.released_at)}</td>
                      <td className="px-3 py-2">{holdReasonText(hold.release_reason || hold.last_error)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </DataSection>

          </div>
        )}
      </div>
    </div>
  );
}

function DataSection({ title, empty, children }) {
  const rows = children?.props?.children?.[1]?.props?.children || [];
  const isEmpty = Array.isArray(rows) && rows.length === 0;

  return (
    <div className="space-y-3">
      <SectionTitle>{title}</SectionTitle>
      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white">
        {isEmpty ? <div className="px-4 py-6 text-center text-sm text-slate-500">{empty}</div> : children}
      </div>
    </div>
  );
}
